// UAT-013 (user-POV test 2026-10-06, CRITICAL) — the plan's ANSI/ASSP
// A10.48-2023 compliance claim must be STATE-AWARE. The editor footer (and
// any saved-plan banner) may declare compliance ONLY when the plan is
// classified AND signed off AND no compliance step is still pending.
// Everything else is an honest draft/pending banner that says WHAT is
// missing and HOW to complete it — never a blanket "Compliant" label.
import type { ComplianceStep, ClassificationResult } from '@/types/planData';

export interface PlanComplianceState {
  state: 'compliant' | 'draft';
  /** Short headline for the footer/banner. */
  label: string;
  /** Full why+how copy for the draft banner. */
  detail: string;
  /** What is missing (drives the draft banner's fix list). */
  missing: string[];
}

export function planComplianceStatus(input: {
  classification: ClassificationResult | null | undefined;
  signed: boolean;
  steps?: ComplianceStep[] | null;
}): PlanComplianceState {
  const missing: string[] = [];
  if (!input.classification?.selected) {
    missing.push(
      'Construction classification not completed — run the Construction Classification wizard (§4.4) and save the plan.'
    );
  }
  const pending = (input.steps ?? []).filter((s) => s.status === 'pending');
  if (pending.length > 0) {
    missing.push(
      `${pending.length} compliance step${pending.length > 1 ? 's' : ''} still pending (${pending
        .map((s) => s.ref)
        .join(', ')}) — complete them, or mark each documented/not-applicable with evidence.`
    );
  }
  if (!input.signed) {
    missing.push(
      'Plan not signed off — the qualified/competent-person sign-off (Final Sign-Offs → Sign-off) must be completed before the plan is used for hoisting.'
    );
  }
  // LUNA-PLANS-001 (delta audit 2026-10-06, MEDIUM): absent compliance-step
  // data is NOT "zero pending" — compliance cannot be verified from evidence
  // that was never recorded. When no steps exist at all, the plan stays a
  // draft naming exactly what is missing, and the Compliant label is never
  // shown on absent data.
  const stepsRecorded = Array.isArray(input.steps) && input.steps.length > 0;
  if (!stepsRecorded && missing.length === 0) {
    missing.push(
      'Compliance steps not recorded — no §-compliance steps were saved for this plan. Complete the compliance checklist and record every step as documented or not-applicable (with evidence) before the plan is used for hoisting.'
    );
  }
  if (missing.length === 0) {
    return {
      state: 'compliant',
      label: 'ANSI/ASSP A10.48-2023 Compliant Construction Plan',
      detail: '',
      missing: [],
    };
  }
  return {
    state: 'draft',
    label: 'DRAFT — not yet ANSI/ASSP A10.48-2023 compliant',
    detail:
      'This plan is a draft. It becomes a compliant construction plan only after: ' +
      missing.join(' '),
    missing,
  };
}
